// Grade de avaliação (VF-1) — pura.
//
// ★ ESTA AGENDA É A FONTE DA VERDADE, não um espelho.
//
// A avaliação de lead vindo de tráfego pago é marcada DENTRO do OdontoSDR, e
// quem define quantas vagas existem por dia e horário é a clínica, aqui. Por
// isso a tela pode dizer "livre" sem ressalva — diferente da agenda da recepção,
// que reflete o Careflow e nunca poderia afirmar isso.
//
// A grade tem DOIS números por linha: o horário e quantas pessoas cabem. Nada de
// cadeira, profissional com agenda própria ou duração de procedimento — cada um
// deles transformaria a peça em outra, e nenhum é necessário para responder
// "tenho vaga quinta às 10h?".

/**
 * Ocupa vaga tudo o que não foi cancelado.
 *
 * ★ VF-3 — LISTA DE NEGAÇÃO, e não de permissão. Antes era
 * `OCUPAM = ['agendado','compareceu','faltou']`: uma lista de permissão cujo
 * comentário descrevia uma lista de negação. Os dois coincidiam por acaso, e
 * qualquer estado novo cairia FORA da lista — a consulta deixaria de ocupar a
 * vaga, a grade reofereceria o horário, e dois pacientes cairiam no mesmo slot.
 * Um erro em silêncio, cujo custo é overbooking.
 *
 * Escrito como negação, o código passa a dizer o que o comentário promete, e o
 * padrão seguro (ocupa) vale para o que ainda não existe.
 */
const NAO_OCUPAM = new Set(['cancelado']);

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 'AAAA-MM-DD' pelo calendário LOCAL — nunca `toISOString()`, que é UTC. */
export function chaveDia(data) {
  const d = paraData(data);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 'HH:MM' pelo relógio local. */
export function horaDe(data) {
  const d = paraData(data);
  if (!d) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const horarioValido = (h) => typeof h?.hora === 'string' && /^\d{2}:\d{2}$/.test(h.hora);

const normalizar = (lista) =>
  (Array.isArray(lista) ? lista : [])
    .filter(horarioValido)
    .map((h) => ({ hora: h.hora, vagas: Math.max(0, Math.trunc(Number(h.vagas) || 0)) }))
    .sort((a, b) => a.hora.localeCompare(b.hora));

/**
 * A grade de um dia: a exceção da data, se houver; senão a do dia da semana.
 *
 * ★ EXCEÇÃO SUBSTITUI, NÃO SOMA. Somar exigiria uma segunda regra ("e se as duas
 * listas tiverem o mesmo horário?") e produziria estados que ninguém prevê
 * olhando a tela. Substituição se lê de uma vez: *neste dia, é isto*.
 *
 * ⚠ `[]` É FECHADO; a AUSÊNCIA da chave é "herde o dia da semana". A diferença
 * decide se o feriado fecha ou se o dia volta a abrir sozinho — tratar
 * `undefined` como lista vazia fecharia a clínica inteira em silêncio.
 */
const bloqueioValido = (b) =>
  /^\d{2}:\d{2}$/.test(b?.de) && /^\d{2}:\d{2}$/.test(b?.ate) && b.de < b.ate;

const normalizarBloqueios = (lista) =>
  (Array.isArray(lista) ? lista : [])
    .filter(bloqueioValido)
    .map((b) => ({ de: b.de, ate: b.ate, motivo: String(b.motivo ?? '').trim() }));

/**
 * A hora cai dentro do bloqueio?
 *
 * ★ INTERVALO SEMIABERTO `[de, ate)`. Uma reunião das 14h às 16h ocupa 14:00,
 * 14:30, 15:00 e 15:30 — e **16:00 continua livre**, porque às 16h a reunião
 * acabou. Fechar o 16:00 junto tiraria da clínica uma vaga que existe, todo dia
 * em que houvesse bloqueio.
 */
export const dentroDoBloqueio = (hora, bloqueio) =>
  typeof hora === 'string' && hora >= bloqueio.de && hora < bloqueio.ate;

/**
 * A grade de um dia: a exceção da data, se houver; senão a do dia da semana.
 *
 * ★ EXCEÇÃO SUBSTITUI, NÃO SOMA. Somar exigiria uma segunda regra ("e se as duas
 * listas tiverem o mesmo horário?") e produziria estados que ninguém prevê
 * olhando a tela. Substituição se lê de uma vez: *neste dia, é isto*.
 *
 * ⚠ `[]` É FECHADO; a AUSÊNCIA da chave é "herde o dia da semana". A diferença
 * decide se o feriado fecha ou se o dia volta a abrir sozinho — tratar
 * `undefined` como lista vazia fecharia a clínica inteira em silêncio.
 *
 * ★ VF-3 (correção) — AS QUATRO FORMAS, e a que quase repetiu o BUG 1:
 *
 *   chave ausente            herda o dia da semana
 *   `horarios: []`           fechado o dia inteiro
 *   `horarios: [...]`        substitui a lista do dia
 *   `bloqueios` SEM horarios HERDA a lista do dia e remove as horas da faixa
 *
 * ⚠ A quarta é a delicada. Antes desta correção, uma exceção cuja CHAVE existe
 * mas que não traz `horarios` caía em `normalizar(undefined)` → `[]` → **dia
 * inteiro fechado**. Gravar um bloqueio de duas horas fecharia o dia todo — o
 * mesmo estrago que fechou a semana da clínica, por outro caminho. Por isso a
 * distinção é feita com `hasOwnProperty`, e não com "o valor é falsy".
 *
 * `bloqueadas` volta junto — as horas que EXISTEM e foram bloqueadas hoje, com o
 * motivo. Sem isso a tela não teria como diferenciá-las de "sem vaga
 * configurada" (que significa "nunca existiu"), e o motivo se perderia.
 */
/**
 * A recorrência é VÁLIDA? Prazo é obrigatório — e é decisão de desenho.
 *
 * ★ Recorrência sem fim é a que ninguém lembra de desligar: ela vai bloqueando
 * a agenda para sempre, e o motivo ("reunião de equipe") deixa de ser verdade
 * muito antes de alguém notar. Sem `validoAte` a regra não é aplicada e a tela
 * não deixa gravar.
 */
export const recorrenciaValida = (r) =>
  Number.isInteger(Number(r?.diaSemana)) &&
  Number(r.diaSemana) >= 0 &&
  Number(r.diaSemana) <= 6 &&
  bloqueioValido(r) &&
  /^\d{4}-\d{2}-\d{2}$/.test(r?.validoAte ?? '');

/**
 * As recorrências que valem para ESTA data.
 *
 * Três filtros, e o terceiro é a exceção da exceção: feriado que cai na segunda,
 * semana em que a reunião não houve. Sem ele, tirar UMA ocorrência exigiria
 * apagar a série inteira — e quem faz isso perde o prazo e o histórico.
 */
export function recorrenciasDoDia(config, data) {
  const chave = chaveDia(data);
  const d = paraData(data);
  if (!chave || !d) return [];
  const lista = Array.isArray(config?.recorrencias) ? config.recorrencias : [];
  return lista
    .filter(recorrenciaValida)
    .filter((r) => Number(r.diaSemana) === d.getDay())
    .filter((r) => chave <= r.validoAte)
    .filter((r) => !(Array.isArray(r.excecoes) ? r.excecoes : []).includes(chave))
    .map((r) => ({ de: r.de, ate: r.ate, motivo: String(r.motivo ?? '').trim() }));
}

/**
 * As consultas JÁ MARCADAS que caem sob alguma recorrência.
 *
 * ★ Serve ao aviso na hora de salvar a série. Sem ele, o conflito só apareceria
 * quando alguém abrisse aquela semana na agenda — três semanas depois, quando
 * remarcar já custa uma ligação. A consulta NÃO some da grade (ela aparece
 * dentro da célula bloqueada), mas ninguém deve descobri-la por acaso.
 */
export function consultasSobRecorrencia(config, agendamentos = []) {
  return (agendamentos ?? []).filter((a) => {
    const status = a?.status ?? 'agendado';
    if (status === 'cancelado' || status === 'faltou') return false;
    const hora = horaDe(a?.dataHora);
    if (!hora) return false;
    return recorrenciasDoDia(config, paraData(a?.dataHora)).some((r) => dentroDoBloqueio(hora, r));
  });
}

/**
 * As próximas ocorrências de uma série, dentro do prazo.
 *
 * ★ EXISTE PARA A AÇÃO CARREGAR O PRÓPRIO CONTEXTO. Antes, pular uma semana
 * dependia do campo "Data" do formulário de bloqueio de DIA ÚNICO, lá no topo da
 * seção: era preciso digitar a data num campo distante e clicar num botão sem
 * nenhuma relação visual com ele. A capacidade existia e ninguém chegava nela.
 *
 * Com as datas reais na tela, a pessoa não digita nem calcula: ela vê a segunda
 * que quer e clica. De graça, isto também mostra se a série caiu no dia da
 * semana errado — o que o formulário sozinho não revelava.
 *
 * `pulada` diz quais já estão nas `excecoes` da série, para a lista poder
 * oferecer "desfazer" no mesmo lugar.
 */
export function proximasOcorrencias(serie, quantas = 6, hoje = new Date()) {
  if (!recorrenciaValida(serie)) return [];
  const alvo = Number(serie.diaSemana);
  const d = paraData(hoje) ?? new Date();
  const cursor = new Date(d);
  cursor.setHours(12, 0, 0, 0);
  // Anda até o primeiro dia da semana que casa — hoje conta, se for ele.
  cursor.setDate(cursor.getDate() + ((alvo - cursor.getDay() + 7) % 7));

  const saida = [];
  const puladas = new Set(Array.isArray(serie.excecoes) ? serie.excecoes : []);
  while (saida.length < Math.max(0, quantas)) {
    const chave = chaveDia(cursor);
    if (!chave || chave > serie.validoAte) break;
    saida.push({ data: chave, pulada: puladas.has(chave) });
    cursor.setDate(cursor.getDate() + 7);
  }
  return saida;
}

/** A data final de um prazo, a partir de hoje. Devolve 'AAAA-MM-DD'. */
export function prazoAte(meses, hoje = new Date()) {
  const d = new Date(hoje);
  d.setMonth(d.getMonth() + Number(meses));
  return chaveDia(d);
}

export function gradeDoDia(config, data) {
  const chave = chaveDia(data);
  const excecoes = config?.excecoes ?? {};
  const d = paraData(data);
  const doDiaDaSemana = d ? normalizar(config?.dias?.[String(d.getDay())]) : [];

  let base = doDiaDaSemana;
  let motivo = '';
  let excecao = false;
  let bloqueios = [];
  // ★ ORDEM DE PRECEDÊNCIA (VF-3, correção 3) — escrita, porque é onde
  // recorrência apodrece:
  //   1. exceção da data COM `horarios` (inclusive `[]`) substitui TUDO,
  //      inclusive as recorrências — é o "hoje é isto" que já existia;
  //   2. senão, a lista do dia da semana;
  //   3. menos os `bloqueios` da exceção daquela data;
  //   4. menos as RECORRÊNCIAS que casam.
  // As três primeiras são o comportamento anterior, intocado. A quarta entra por
  // último, o que garante que nada do que já funcionava muda de sentido.
  let substituiu = false;

  if (chave && Object.prototype.hasOwnProperty.call(excecoes, chave)) {
    const e = excecoes[chave];
    excecao = true;
    // Duas formas aceitas: a lista crua (dia com outros horários) e o objeto
    // `{ horarios, motivo }`. O MOTIVO existe porque um dia bloqueado sem
    // explicação faz a SDR ligar para o gestor perguntando: "fechado — reunião
    // de equipe" responde antes da pergunta.
    motivo = Array.isArray(e) ? '' : String(e?.motivo ?? '').trim();
    bloqueios = normalizarBloqueios(Array.isArray(e) ? null : e?.bloqueios);
    const temHorarios =
      Array.isArray(e) || Object.prototype.hasOwnProperty.call(e ?? {}, 'horarios');
    substituiu = temHorarios;
    base = temHorarios ? normalizar(Array.isArray(e) ? e : e.horarios) : doDiaDaSemana;
  } else if (!d) {
    return { horarios: [], excecao: false, motivo: '', bloqueadas: [] };
  }

  // Degrau 4: só quando a data NÃO foi substituída por uma lista própria.
  if (!substituiu) bloqueios = [...bloqueios, ...recorrenciasDoDia(config, data)];

  if (bloqueios.length === 0) return { horarios: base, excecao, motivo, bloqueadas: [] };

  const bloqueadas = [];
  const horarios = base.filter((h) => {
    const b = bloqueios.find((x) => dentroDoBloqueio(h.hora, x));
    if (!b) return true;
    bloqueadas.push({ hora: h.hora, vagas: h.vagas, motivo: b.motivo });
    return false;
  });
  return { horarios, excecao, motivo, bloqueadas };
}

/**
 * 'AAAA-MM-DD' → '10/09/2026', por MANIPULAÇÃO DE STRING.
 *
 * ⚠ NUNCA passe por `Date` aqui. `new Date('2026-09-10')` é lido como
 * meia-noite **UTC**; em UTC-3 isso é 09/09 às 21h, e
 * `toLocaleDateString('pt-BR')` mostraria **09/09/2026** — a data errada, com
 * toda a aparência de estar certa. A chave é texto, e texto é o que ela deve
 * continuar sendo até chegar na tela.
 */
export function dataParaBr(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso ?? '');
}

/**
 * O agendamento consome vaga de AVALIAÇÃO?
 *
 * ⚠ Retorno NÃO consome. A mesma tela marca a primeira avaliação e o retorno de
 * quem já compareceu; contar os dois faria um retorno de negociação ocupar uma
 * vaga de avaliação que está livre.
 *
 * Reserva: agendamento SEM `tipo` conta como avaliação — é o que existia antes
 * do campo, e é o que ele era.
 */
export const consomeVaga = (a) =>
  (a?.tipo ?? 'avaliacao') === 'avaliacao' && !NAO_OCUPAM.has(a?.status ?? 'agendado');

/**
 * A ocupação de um dia: cada horário da grade com quem já está marcado.
 *
 * `fora` são os agendamentos de avaliação do dia que NÃO caem em nenhum horário
 * da grade — marcados pelo escape, ou sobrados de uma grade que mudou depois.
 * Aparecem na tela para o dia não mentir sobre quem vem.
 */
export function ocupacaoDoDia(config, data, agendamentos = []) {
  const { horarios, excecao, motivo } = gradeDoDia(config, data);
  const chave = chaveDia(data);
  const doDia = (agendamentos ?? []).filter((a) => chaveDia(a?.dataHora) === chave);

  const avaliacoes = doDia.filter(consomeVaga);
  const retornos = doDia.filter((a) => (a?.tipo ?? 'avaliacao') !== 'avaliacao');

  const usados = new Set();
  const linhas = horarios.map((h) => {
    const ocupantes = avaliacoes.filter((a) => horaDe(a.dataHora) === h.hora);
    for (const a of ocupantes) usados.add(a.id ?? a);
    const ocupadas = ocupantes.length;
    return {
      hora: h.hora,
      vagas: h.vagas,
      ocupadas,
      livres: Math.max(0, h.vagas - ocupadas),
      cheio: ocupadas >= h.vagas,
      ocupantes: ocupantes.map((a) => ({
        id: a.id, leadId: a.leadId, nome: a.leadNome ?? '', status: a.status ?? 'agendado',
      })),
    };
  });

  const fora = avaliacoes.filter((a) => !usados.has(a.id ?? a));

  return {
    excecao,
    // O texto que a tela de agendar mostra quando o dia está bloqueado.
    motivo,
    // Dia sem NENHUM horário configurado: a clínica não atende avaliação nele.
    fechado: horarios.length === 0,
    linhas,
    fora,
    retornos,
    vagas: linhas.reduce((t, l) => t + l.vagas, 0),
    livres: linhas.reduce((t, l) => t + l.livres, 0),
  };
}

/**
 * O próximo dia com vaga, a partir de uma data.
 *
 * Serve à tela quando o dia escolhido está fechado ou cheio: em vez de só dizer
 * "não dá", ela oferece quando dá. Varre no máximo `limite` dias — sem teto, uma
 * grade toda vazia viraria laço infinito.
 */
export function proximoDiaComVaga(config, agendamentos = [], apartirDe = new Date(), limite = 60) {
  const inicio = paraData(apartirDe);
  if (!inicio) return null;
  for (let i = 0; i < limite; i += 1) {
    const dia = new Date(inicio);
    dia.setDate(dia.getDate() + i);
    dia.setHours(12, 0, 0, 0);
    const o = ocupacaoDoDia(config, dia, agendamentos);
    if (!o.fechado && o.livres > 0) return dia;
  }
  return null;
}

/**
 * ★ A GRADE REAL DA SORRIDENTS, para a aba nascer preenchida.
 *
 * Segunda a sexta: 08:00 às 11:30 e 13:00 às 18:00, de 30 em 30 — **19 vagas**,
 * a última às 18:00. **Sábado: 08:00 às 13:00 corrido — 11 vagas**, porque no
 * sábado NÃO há pausa de almoço (decisão do dono, 07/set). Domingo fechado. Uma
 * vaga por horário, como ponto de partida.
 *
 * ⚠ O ALMOÇO NÃO É UM CONCEITO. Não existe "intervalo" nem "bloqueio de almoço"
 * neste código: no dia útil, 12:00 e 12:30 simplesmente **não estão na lista**;
 * no sábado, estão. A grade diz quais horários existem, e pronto.
 *
 * ★ E é justamente por NÃO haver conceito de intervalo que a diferença entre os
 * dois dias saiu de graça: nenhuma regra nova, só duas listas diferentes. Se
 * houvesse um "bloqueio de almoço", o sábado teria virado exceção dele.
 */
const meiaEmMeia = (deHora, ateHora) => {
  const saida = [];
  for (let m = deHora * 60; m <= ateHora * 60; m += 30) {
    saida.push({ hora: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`, vagas: 1 });
  }
  return saida;
};

const DIA_UTIL = [...meiaEmMeia(8, 11.5), ...meiaEmMeia(13, 18)];
const SABADO = meiaEmMeia(8, 13); // corrido: no sábado não há pausa de almoço

export const GRADE_SUGERIDA = {
  dias: { 1: DIA_UTIL, 2: DIA_UTIL, 3: DIA_UTIL, 4: DIA_UTIL, 5: DIA_UTIL, 6: SABADO, 0: [] },
  excecoes: {},
};

/**
 * O horário cabe no funcionamento da clínica?
 *
 * ⚠ `config.horarioAtendimento` é o HORÁRIO DE FUNCIONAMENTO, usado pelo
 * atendimento automático do WhatsApp — **não é a grade de avaliação**. Um dia
 * aberto das 8h às 19h não quer dizer que há avaliação em todas as horas. Serve
 * só para VALIDAR (não deixar cadastrar vaga com a clínica fechada) e SUGERIR ao
 * montar a grade pela primeira vez. Nunca para decidir vaga.
 */
export function dentroDoFuncionamento(horarioAtendimento, diaSemana, hora) {
  const faixa = horarioAtendimento?.dias?.[String(diaSemana)];
  if (!faixa?.inicio || !faixa?.fim) return null; // não dá para saber ≠ está fora
  return hora >= faixa.inicio && hora <= faixa.fim;
}

/** Sugestão inicial para um dia: a faixa de funcionamento vira horários de hora em hora. */
export function sugerirHorarios(horarioAtendimento, diaSemana, vagas = 1, passoMinutos = 30) {
  const faixa = horarioAtendimento?.dias?.[String(diaSemana)];
  if (!faixa?.inicio || !faixa?.fim) return [];
  const minutos = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const ini = minutos(faixa.inicio);
  const fim = minutos(faixa.fim);
  if (!Number.isFinite(ini) || !Number.isFinite(fim) || fim <= ini) return [];
  const saida = [];
  for (let m = ini; m < fim; m += passoMinutos) {
    saida.push({ hora: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`, vagas });
  }
  return saida;
}
